import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  debounce,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Button from "../../../components/Button";
import { getApprovers, getVendors } from "../../../services/invoiceService";
import { isValidDate } from "../../../helpers/Utils";

const DownloadPOModal = ({ open, onClose, onDownload }) => {
  const initialValues = {
    vendorName: "",
    fromDate: "",
    toDate: "",
  };
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState(initialValues);
  const [vendorList, setVendorList] = useState([{ label: 'All', value: '' }]);
  const [approverList, setApproverList] = useState([{display_name: "All", id: "-1"}]);

  function reset() {
    setValues(initialValues);
    setErrors(initialValues);
  }

  const fetchVendor = React.useMemo(
    () =>
      debounce(async (vendor) => {
        let resp = await getVendors({ query: vendor });
        resp = resp?.map((vendor) => ({label: vendor, value: vendor}));
        setVendorList(resp ? resp : []);
      }, 400),
    []
  );

  const fetchApprovers = async () => {
    try {
      let resp = await getApprovers();
      resp = [{display_name: "All", id: "-1"}, ...resp];
      setApproverList(resp ? resp : []);
    } catch (err) {
      setErrors((prev) => ({ ...prev, approver: "Failed fetching approver list" }));
    }
  }

  useEffect(() => {
    if (values.vendorName && values.vendorName.length > 2) {
      fetchVendor(values.vendorName);
    } else {
      setVendorList([{ label: 'All', value: '' }]);
    }
  }, [values.vendorName, fetchVendor]);

  useEffect(() => {
    fetchApprovers();
  }, []);

  function handleInputChange(e) {
    let { name, value, type } = e?.target;
    setValues({
      ...values,
      [name]: value,
    });
  }

  function validate(e, validation) {
    let { name, value } = e?.target;

    if (!validation(value)) {
      setErrors((prev) => ({ ...prev, [name]: "Invalid value" }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleDownload(values) {
    let payload = { 
      fromDate: values.fromDate,
      toDate: values.toDate,
      approver: values.approver === "-1" ? undefined : values.approver,
      vendorName: values.vendorName.value
    };

    onDownload(payload);
    handleClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Download Data</DialogTitle>
      <DialogContent dividers>
        <DialogContentText mb={1}>Select Vendor</DialogContentText>
        <Autocomplete
          filterOptions={(x) => x}
          options={vendorList}
          autoComplete
          clearOnBlur
          includeInputInList
          filterSelectedOptions
          value={values.vendorName}
          noOptionsText={
            values.vendorName && values.vendorName.length > 2
              ? "No Results"
              : "Enter atleast 3 character"
          }
          onChange={(event, newValue) => {
            setValues((prev) => ({ ...prev, vendorName: newValue || "" }));
          }}
          onInputChange={(event, newInputValue) => {
            setValues((prev) => ({ ...prev, vendorName: newInputValue || "" }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type atleast 3 characters"
              fullWidth
              name="vendorName"
              error={errors.vendorName.length > 1}
              helperText={errors.vendorName}
              onBlur={(e) => validate(e, (d) => d.length > 1)}
            />
          )}
        />
        <DialogContentText mt={2} mb={1}>Select Approver</DialogContentText>
        <Select
          id="approverName"
          value={values.approver}
          onChange={(e) => setValues((prev) => ({ ...prev, approver: e.target.value || "" }))}
          fullWidth
          error={errors.approver}
          defaultValue="-1"
        >
          {approverList.map((item) => {
            return <MenuItem key={item.id} value={item.id}>{item.display_name}</MenuItem>
          })}
        </Select>
        {errors.approver && <FormHelperText error>{errors.approver}</FormHelperText>}
        <DialogContentText mt={2} mb={1}>
          Invoice Approval date
        </DialogContentText>
        <Stack direction={"row"} gap={"24px"}>
          <TextField
            margin="normal"
            name="fromDate"
            error={errors.fromDate.length > 1}
            helperText={errors.fromDate}
            label="From"
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            onChange={handleInputChange}
            onBlur={(e) => validate(e, (d) => isValidDate(d))}
            value={values.fromDate}
          />
          <TextField
            margin="normal"
            name="toDate"
            label="To"
            error={errors.toDate.length > 1}
            helperText={errors.toDate}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            onChange={handleInputChange}
            onBlur={(e) => validate(e, (d) => isValidDate(d))}
            value={values.toDate}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type={"primary"}
          disabled={!values.vendorName || !values.fromDate || !values.toDate}
          onClick={() => {
            if (!Object.values(errors).some((x) => x)) {
              handleDownload(values);
            }
          }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadPOModal;
